import { css, html, LitElement } from 'lit';

import '@brightspace-ui/core/components/typography/typography.js';
import '@brightspace-ui/core/components/colors/colors.js';
import '@brightspace-ui/core/components/alert/alert-toast';
import '@brightspace-ui/core/components/icons/icon.js';

import routes from './app-routes.js';
import { router } from 'lit-element-router';

import { heading1Styles } from '@brightspace-ui/core/components/typography/styles';
import { ProviderMixin } from '@brightspace-ui/core/mixins/provider-mixin';

import { PMClient } from './client.js';

class Main extends ProviderMixin(router(LitElement)) {

  static get properties() {
    return {
      loading: { type: Boolean },
      _alert: { type: String },
      _alertOpen: { type: Boolean },
      view: { type: Object },
    };
  }

  static get styles() {
    return [
      heading1Styles,
      css`
        :host {
          display: block;
          height: 100%;
          --shell-accent: #7c3aed;
          --shell-accent2: #6d28d9;
          --shell-text: #1f2937;
          --shell-muted: #6b7280;
        }

        main {
          max-width: 1280px;
          margin: 0 auto;
          width: 100%;
          height: 100%;
          padding: 0 18px;
        }

        .loading-title {
          padding-top: 10px;
        }

        .loading-container {
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          text-align: center;
          min-height: 100vh;
        }

        /* New header styles */
        .app-header {
          background: #ffffff;
          border-bottom: 1px solid #e5e7eb;
          position: sticky;
          top: 0;
          z-index: 50;
        }

        .header-progress {
          height: 4px;
          background: linear-gradient(90deg, #10b981, #34d399, #6ee7b7);
        }

        .header-inner {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 16px;
          padding: 10px 18px;
          max-width: 1280px;
          margin: 0 auto;
        }

        .header-left {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .home-link {
          text-decoration: none;
          color: inherit;
          display: inline-flex;
          align-items: center;
        }

        .logo {
          height: 52px;
          cursor: pointer;
          transition: transform 0.15s ease;
        }

        .logo:hover {
          transform: scale(1.02);
        }

        .header-separator {
          width: 1px;
          height: 32px;
          background: #e5e7eb;
          margin: 0 4px;
        }

        .app-title {
          font-size: 18px;
          font-weight: 700;
          color: var(--shell-text);
          margin: 0;
          cursor: pointer;
          transition: color 0.15s ease;
        }

        .app-title:hover {
          color: var(--shell-accent);
        }

        .header-right {
          display: flex;
          align-items: center;
          gap: 6px;
        }

        .nav-link {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 8px 14px;
          border-radius: 10px;
          text-decoration: none;
          color: var(--shell-muted);
          font-size: 14px;
          font-weight: 600;
          transition: background 0.15s ease, color 0.15s ease;
          border: 1px solid transparent;
        }

        .nav-link:hover {
          background: #f3f4f6;
          color: var(--shell-text);
        }

        .nav-link.active {
          background: rgba(124, 58, 237, 0.08);
          color: var(--shell-accent);
          border-color: rgba(124, 58, 237, 0.2);
        }

        .nav-link d2l-icon {
          color: inherit;
        }

        .nav-separator {
          width: 1px;
          height: 24px;
          background: #e5e7eb;
          margin: 0 6px;
        }

        .external-link {
          color: var(--shell-muted);
        }

        .external-link:hover {
          color: var(--shell-text);
          background: #f9fafb;
        }

        html {
          box-sizing: border-box;
        }

        *, *::after, *::before {
          box-sizing: inherit;
        }

        *,
        *:before,
        *:after {
          box-sizing: border-box;
          outline: none;
        }

        html {
          font-family: ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif;
          font-size: 16px;
          -webkit-font-smoothing: antialiased;
          -moz-osx-font-smoothing: grayscale;
          font-weight: 400;
          line-height: 1.5;
          color: #1f2937;
        }

        body {
          position: relative;
          display: flex;
          align-items: center;
          justify-content: center;
          width: 100%;
          height: 100vh;
        }

        .loader {
          position: relative;
          display: grid;
          grid-template-columns: 33% 33% 33%;
          grid-gap: 2px;
          width: 100px;
          height: 100px;
        }
        .loader > div {
          position: relative;
          width: 100%;
          height: 100%;
          background: #7c3aed;
          transform: scale(0);
          transform-origin: center center;
          animation: loader 2s infinite linear;
        }
        .loader > div:nth-of-type(1), .loader > div:nth-of-type(5), .loader > div:nth-of-type(9) {
          animation-delay: 0.4s;
        }
        .loader > div:nth-of-type(4), .loader > div:nth-of-type(8) {
          animation-delay: 0.2s;
        }
        .loader > div:nth-of-type(2), .loader > div:nth-of-type(6) {
          animation-delay: 0.6s;
        }
        .loader > div:nth-of-type(3) {
          animation-delay: 0.8s;
        }

        @keyframes loader {
          0% {
            transform: scale(0);
          }
          40% {
            transform: scale(1);
          }
          80% {
            transform: scale(1);
          }
          100% {
            transform: scale(0);
          }
        }
    `];
  }

  constructor() {
    super();
    this.view = null;
    this.loading = true;
    this._alertOpen = false;
    this.client = new PMClient();
    this.provideInstance('d2l-pm-client', this.client);
  }

  static get routes() {
    return routes;
  }

  connectedCallback() {
    super.connectedCallback();
    this.style.setProperty('--d2l-branding-primary-color', '#72FF77');
    this.style.setProperty('--d2l-color-celestine-minus-1', '#72FF77');
    this.style.setProperty('--d2l-color-celestine', '#AE00FF');
    document.addEventListener('kraken-alert-event', async e => {
      const message = e.detail.message || 'Please try again. If the problem persists, please contact support.';
      const type = e.detail.type || 'critical';
      this._openAlert(type, message);
    });
  }

  async firstUpdated() {
    try {
      if (!this.client.isDev()) this.session = this.session || (await this.client.fetchSession());
      this.client.apiUrl = this.session?.apiUrl;
      document.title = this.session.appName;
      this.loading = false;
    } catch {
      const relayState = encodeURIComponent(window.location.pathname);
      location.href = `/api/auth?relayState=${relayState}`;
    }
  }

  router(route, params, query, data) {
    this.view = data.view({
      params,
      search: query,
    });
  }

  _loadingTemplate() {
    return html`
      <main class="loading-container">
        <div class="loader">
          <div></div>
          <div></div>
          <div></div>
          <div></div>
          <div></div>
          <div></div>
          <div></div>
          <div></div>
          <div></div>
        </div>
        <div class="loading-title">Releasing the Kraken...</div>
      </main>
    `;
  }

  _mainTemplate() {
    return html`
        ${this._headerTemplate()}
        <main>
            ${this.view}
          <d2l-alert-toast id="alert">${this._alert}</d2l-alert-toast>
        </main>
      `;
  }

  render() {
    return this.loading || !this.view ? this._loadingTemplate() : this._mainTemplate();
  }

  _openAlert(type, message) {
    this._alert = message;
    const alertTemplate = this.shadowRoot.getElementById('alert');
    if (alertTemplate) {
      alertTemplate.type = type;
      alertTemplate.open = true;
    }
  }

  _headerTemplate() {
    const links = Array.isArray(this.session?.links) ? this.session.links : [];
    const currentPath = window.location.pathname;

    return html`
      <header class="app-header">
        <div class="header-progress"></div>
        <div class="header-inner">
          <div class="header-left">
            <a href="/" class="home-link">
              <img class="logo" src="assets/img/kraken.png" alt="Home">
            </a>
            <div class="header-separator"></div>
            <a href="/" class="home-link">
              <h1 class="app-title">${this.session.appName}</h1>
            </a>
          </div>
          <nav class="header-right">
            <a href="/home" class="nav-link ${currentPath === '/home' ? 'active' : ''}">
              <d2l-icon icon="tier1:home"></d2l-icon>
              Home
            </a>
            <a href="/" class="nav-link ${currentPath === '/' ? 'active' : ''}">
              <d2l-icon icon="tier1:locations"></d2l-icon>
              Environments
            </a>
            ${links.length > 0 ? html`
              <div class="nav-separator"></div>
              ${links.map(link => html`
                <a
                  href="${link.url}"
                  class="nav-link external-link"
                  target="_blank"
                  rel="noopener noreferrer">
                  <d2l-icon icon="${link.icon || 'tier1:link'}"></d2l-icon>
                  ${link.name}
                </a>
              `)}
            ` : ''}
          </nav>
        </div>
      </header>
    `;
  }

}

window.customElements.define('app-main', Main);
